import { useState } from 'react';
import {
  deleteRaw,
  deleteSupplier,
  poAdvance,
  rawIssue,
  rawReceive,
  supplyOverview,
  type PlanRow,
  type RawTile,
  type StockMove,
  type Supplier,
} from './logistics-api';
import { NewPOModal } from './NewPOModal';
import { StockMoveModal } from './StockMoveModal';
import { RawEditModal } from './RawEditModal';
import { SupplierEditModal } from './SupplierEditModal';
import { money } from './format';
import { downloadCSV, type Col } from './export-utils';
import { useData } from './useData';

const poTag = (s: string) =>
  s === 'На складе' ? 'grn' : s === 'В пути' ? 'cy' : s === 'Оплачен' ? 'gold' : '';

const MOVE_COLS: Col<StockMove>[] = [
  { header: 'Когда', value: (m) => m.at },
  { header: 'Позиция', value: (m) => m.raw_name },
  { header: 'Операция', value: (m) => (m.kind === 'receive' ? 'приход' : 'списание') },
  { header: 'Кол-во', value: (m) => `${m.delta > 0 ? '+' : ''}${m.delta} ${m.unit}` },
  { header: 'Сделка', value: (m) => m.deal_no ?? '' },
  { header: 'Кто', value: (m) => m.user_name ?? '' },
];

export function SupplyScreen() {
  const { data, refresh } = useData('supply', supplyOverview);
  const [busy, setBusy] = useState(false);
  const [poOpen, setPoOpen] = useState(false);
  const [presetSup, setPresetSup] = useState<number | undefined>(undefined);
  const [poPreset, setPoPreset] = useState<
    { what?: string; raw_id?: number | null; qty_num?: number } | undefined
  >(undefined);
  const [move, setMove] = useState<{ raw: RawTile; dir: 'receive' | 'issue' } | null>(null);
  const [rawEdit, setRawEdit] = useState<RawTile | 'new' | null>(null);
  const [supEdit, setSupEdit] = useState<Supplier | 'new' | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  async function act(p: Promise<unknown>) {
    setBusy(true);
    setMsg(null);
    try {
      await p;
      await refresh();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Ошибка');
    } finally {
      setBusy(false);
    }
  }

  function delRaw(r: RawTile) {
    if (window.confirm(`Удалить позицию склада «${r.name}»? Движения по ней будут удалены.`))
      act(deleteRaw(r.id));
  }
  function delSup(s: Supplier) {
    if (window.confirm(`Удалить поставщика «${s.name}»?`)) act(deleteSupplier(s.id));
  }

  function openNewPO(supplierId?: number) {
    setPresetSup(supplierId);
    setPoPreset(undefined);
    setPoOpen(true);
  }
  // Заявка из «Плана закупа»: подставляем позицию, привязку к складу и дефицит.
  function requestFor(p: PlanRow) {
    setPresetSup(undefined);
    setPoPreset({
      what: p.component,
      raw_id: p.raw_id,
      qty_num: p.deficit && p.deficit > 0 ? p.deficit : undefined,
    });
    setPoOpen(true);
  }
  function closePO() {
    setPoOpen(false);
    setPoPreset(undefined);
  }

  if (!data) return <p className="muted">Загрузка…</p>;

  return (
    <div className="scr">
      <div className="scr-head">
        <h2>Закуп и оборотный склад</h2>
        <span className="muted">поставщики · сырьё · декларации</span>
        <button className="btn sm" style={{ marginLeft: 'auto' }} onClick={() => openNewPO()}>
          + Заявка поставщику
        </button>
      </div>

      {msg && <p className="err">{msg}</p>}

      <div className="section-t" style={{ display: 'flex', alignItems: 'center' }}>
        Оборотный склад · сырьё и комплектующие
        <button
          className="btn ghost sm"
          style={{ marginLeft: 'auto' }}
          disabled={busy}
          onClick={() => setRawEdit('new')}
        >
          + Позиция
        </button>
      </div>
      <div className="grid-cards">
        {data.raw.map((r) => (
          <div
            className="catcard"
            key={r.id}
            style={r.short ? { borderColor: 'rgba(216,70,54,.45)' } : undefined}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <div className="cn grow">{r.name}</div>
              <button
                className="icon-btn"
                disabled={busy}
                title="Правка позиции"
                onClick={() => setRawEdit(r)}
              >
                ✎
              </button>
              <button
                className="icon-btn"
                disabled={busy}
                title="Удалить позицию"
                onClick={() => delRaw(r)}
              >
                🗑
              </button>
            </div>
            <div className="rmeta">
              норма {r.min} {r.unit}
            </div>
            <div
              className="mono"
              style={{
                fontSize: 22,
                fontWeight: 700,
                margin: '6px 0',
                color: r.short ? 'var(--red)' : 'var(--tx)',
              }}
            >
              {r.qty}{' '}
              <span className="muted" style={{ fontSize: 12 }}>
                {r.unit}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className={'chip ' + (r.short ? 'red' : 'grn')}>
                {r.short ? 'нехватка' : 'в норме'}
              </span>
              <span className="qty">
                <button
                  disabled={busy}
                  title={`Быстрое списание в цех (−${r.step} ${r.unit})`}
                  onClick={() => act(rawIssue(r.id))}
                >
                  −
                </button>
                <button
                  disabled={busy}
                  title={`Быстрый приход (+${r.step} ${r.unit})`}
                  onClick={() => act(rawReceive(r.id))}
                >
                  ＋
                </button>
              </span>
            </div>
            <button
              className="btn ghost sm"
              style={{ marginTop: 8, width: '100%' }}
              disabled={busy}
              title="Приход/списание точным количеством и привязкой к сделке"
              onClick={() => setMove({ raw: r, dir: 'receive' })}
            >
              Движение…
            </button>
          </div>
        ))}
      </div>

      <div className="section-t">План закупа · потребность под заказы в производстве</div>
      <p className="muted small" style={{ margin: '0 0 8px' }}>
        Потребность рассчитана по BOM (спецификация — эвристика по названию изделия до реальных
        спецификаций завода/1С). Сопоставление со складом — по ключевому слову; дефицит считается
        только для штучных позиций.
      </p>
      <div className="tablewrap glass">
        <table className="tbl card-table">
          <thead>
            <tr>
              <th>Комплектующая</th>
              <th>Под заказы</th>
              <th className="r">Нужно</th>
              <th className="r">На складе</th>
              <th className="r">Дефицит</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.plan.map((p) => (
              <tr key={p.component}>
                <td data-label="Комплектующая">
                  <b>{p.component}</b>
                  {p.raw_name && <div className="rmeta">склад: {p.raw_name}</div>}
                </td>
                <td data-label="Под заказы" className="mono muted">
                  {p.deals.join(', ')}
                </td>
                <td data-label="Нужно" className="r mono">
                  {p.need}
                </td>
                <td data-label="На складе" className="r mono muted">
                  {p.on_stock != null ? `${p.on_stock} ${p.unit ?? ''}` : '—'}
                </td>
                <td data-label="Дефицит" className="r mono">
                  {p.deficit == null ? (
                    <span className="muted">—</span>
                  ) : p.deficit > 0 ? (
                    <span style={{ color: 'var(--red)', fontWeight: 700 }}>
                      {p.deficit} {p.unit}
                    </span>
                  ) : (
                    <span className="chip grn">хватает</span>
                  )}
                </td>
                <td className="r">
                  <button className="btn ghost sm" disabled={busy} onClick={() => requestFor(p)}>
                    Заявка
                  </button>
                </td>
              </tr>
            ))}
            {data.plan.length === 0 && (
              <tr>
                <td colSpan={6} className="muted">
                  Нет заказов в производстве — потребность не рассчитывается.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="section-t">Заявки поставщикам</div>
      <div className="tablewrap glass">
        <table className="tbl card-table">
          <thead>
            <tr>
              <th>Поставщик / позиция</th>
              <th>Кол-во</th>
              <th className="r">Сумма</th>
              <th>Статус</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.po.map((p) => (
              <tr key={p.id} style={{ cursor: 'default' }}>
                <td data-label="Позиция">
                  <b>{p.what}</b>
                  <div className="rmeta">
                    {[p.supplier, p.deal_no].filter(Boolean).join(' · ') || '—'}
                  </div>
                </td>
                <td data-label="Кол-во" className="mono muted">
                  {p.qty_label ?? '—'}
                  {p.raw_id != null && (
                    <span
                      className="chip cy"
                      style={{ marginLeft: 6 }}
                      title="Оприходование пополнит склад"
                    >
                      склад
                    </span>
                  )}
                </td>
                <td data-label="Сумма" className="r mono">
                  {p.amount != null ? money(p.amount) : '—'}
                </td>
                <td data-label="Статус">
                  <span className={'chip ' + poTag(p.status)}>{p.status}</span>
                </td>
                <td className="r">
                  {p.next ? (
                    <button
                      className="btn ghost sm"
                      disabled={busy}
                      onClick={() => act(poAdvance(p.id))}
                    >
                      {p.next === 'На складе' ? 'Провести на склад' : '→ ' + p.next}
                    </button>
                  ) : (
                    <span className="chip grn">оприходовано ✓</span>
                  )}
                </td>
              </tr>
            ))}
            {data.po.length === 0 && (
              <tr>
                <td colSpan={5} className="muted">
                  Заявок нет. Создайте первую кнопкой «+ Заявка поставщику».
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="section-t" style={{ display: 'flex', alignItems: 'center' }}>
        Поставщики
        <button
          className="btn ghost sm"
          style={{ marginLeft: 'auto' }}
          disabled={busy}
          onClick={() => setSupEdit('new')}
        >
          + Поставщик
        </button>
      </div>
      <div>
        {data.suppliers.map((s) => (
          <div className="row" key={s.id}>
            <div className="grow">
              <div className="rname">{s.name}</div>
              <div className="rmeta">{s.cat}</div>
            </div>
            {s.country && <span className="chip">{s.country}</span>}
            <button
              className="btn ghost sm"
              style={{ marginLeft: 6 }}
              onClick={() => openNewPO(s.id)}
            >
              Заявка
            </button>
            <button
              className="icon-btn"
              style={{ marginLeft: 4 }}
              disabled={busy}
              title="Правка поставщика"
              onClick={() => setSupEdit(s)}
            >
              ✎
            </button>
            <button
              className="icon-btn"
              disabled={busy}
              title="Удалить поставщика"
              onClick={() => delSup(s)}
            >
              🗑
            </button>
          </div>
        ))}
        {data.suppliers.length === 0 && (
          <p className="muted" style={{ padding: 10 }}>
            Поставщиков нет. Добавьте кнопкой «+ Поставщик».
          </p>
        )}
      </div>

      <div className="section-t" style={{ display: 'flex', alignItems: 'center' }}>
        Журнал движений склада
        <button
          className="btn ghost sm"
          style={{ marginLeft: 'auto' }}
          disabled={data.movements.length === 0}
          onClick={() => downloadCSV('движения-склада', MOVE_COLS, data.movements)}
          title="Скачать CSV (Excel)"
        >
          ⬇ CSV
        </button>
      </div>
      <div className="tablewrap glass">
        <table className="tbl card-table">
          <thead>
            <tr>
              <th>Когда</th>
              <th>Позиция</th>
              <th>Операция</th>
              <th className="r">Кол-во</th>
              <th>Сделка</th>
              <th>Кто</th>
            </tr>
          </thead>
          <tbody>
            {data.movements.map((m) => (
              <tr key={m.id}>
                <td data-label="Когда" className="mono muted">
                  {m.at}
                </td>
                <td data-label="Позиция">{m.raw_name}</td>
                <td data-label="Операция">
                  <span className={'chip ' + (m.kind === 'receive' ? 'grn' : 'gold')}>
                    {m.kind === 'receive' ? 'приход' : 'списание'}
                  </span>
                </td>
                <td data-label="Кол-во" className="r mono" style={{ whiteSpace: 'nowrap' }}>
                  {m.delta > 0 ? '+' : ''}
                  {m.delta} {m.unit}
                </td>
                <td data-label="Сделка" className="mono muted">
                  {m.deal_no ?? '—'}
                </td>
                <td data-label="Кто" className="muted">
                  {m.user_name ?? '—'}
                </td>
              </tr>
            ))}
            {data.movements.length === 0 && (
              <tr>
                <td colSpan={6} className="muted">
                  Движений пока нет. Приход/списание появятся здесь.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {move && (
        <StockMoveModal
          raw={move.raw}
          deals={data.deals}
          dir={move.dir}
          onClose={() => setMove(null)}
          onDone={() => {
            setMove(null);
            refresh();
          }}
        />
      )}

      {poOpen && (
        <NewPOModal
          suppliers={data.suppliers}
          deals={data.deals}
          raw={data.raw}
          presetSupplierId={presetSup}
          preset={poPreset}
          onClose={closePO}
          onCreated={() => {
            closePO();
            refresh();
          }}
        />
      )}

      {rawEdit && (
        <RawEditModal
          item={rawEdit === 'new' ? undefined : rawEdit}
          onClose={() => setRawEdit(null)}
          onSaved={() => {
            setRawEdit(null);
            refresh();
          }}
        />
      )}

      {supEdit && (
        <SupplierEditModal
          item={supEdit === 'new' ? undefined : supEdit}
          onClose={() => setSupEdit(null)}
          onSaved={() => {
            setSupEdit(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}
